import type { SalesOrderStatus } from "@/features/sales-orders/types";

export const SO_STATUSES: { value: SalesOrderStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "confirmed", label: "Confirmed" },
  { value: "in_progress", label: "In progress" },
  { value: "partial", label: "Partial" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
];

export function labelFor(
  options: { value: string; label: string }[],
  value: string | null | undefined,
) {
  if (!value) return "—";
  return options.find((o) => o.value === value)?.label ?? value;
}

export function statusTone(
  status: SalesOrderStatus,
): "success" | "warning" | "danger" | "neutral" {
  switch (status) {
    case "confirmed":
    case "closed":
      return "success";
    case "in_progress":
    case "partial":
      return "warning";
    case "cancelled":
      return "danger";
    default:
      return "neutral";
  }
}

export function canEditLines(status: SalesOrderStatus) {
  return status === "draft";
}

export function canEditHeader(status: SalesOrderStatus) {
  return status === "draft" || status === "confirmed";
}

/** Tolerance when comparing ordered vs fulfilled weight */
export const FULFILL_EPSILON = 0.0001;

export function canFulfill(status: SalesOrderStatus) {
  return status === "confirmed" || status === "in_progress" || status === "partial";
}

export function orderedBasis(line: { qty: number; weight: number }) {
  return line.weight > 0 ? line.weight : line.qty;
}

export function remainingWeight(line: {
  qty: number;
  weight: number;
  fulfilled_weight: number;
}) {
  return Math.max(0, orderedBasis(line) - line.fulfilled_weight);
}

export function remainingQty(line: {
  qty: number;
  weight: number;
  fulfilled_qty: number;
}) {
  if (line.qty <= 0) return 0;
  return Math.max(0, line.qty - line.fulfilled_qty);
}

export function isLineFullyFulfilled(line: {
  qty: number;
  weight: number;
  fulfilled_qty: number;
  fulfilled_weight: number;
}) {
  const remWt = remainingWeight(line);
  if (remWt > FULFILL_EPSILON) return false;
  if (line.qty > 0 && remainingQty(line) > FULFILL_EPSILON) return false;
  return true;
}
